import { formatCurrency, formatDate } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  CardBodyComponent,
  CardComponent,
  StateMessageComponent,
  TermValueComponent,
  type TermValueItem,
} from '@groundworks/ngx-groundworks-ui';
import { PaymentLineItem, PaymentSummary } from '../../sdk/sdk';
import { PaymentService } from '../../services/payment.service';
import { ModalService } from '../../services/modal.service';
import { ConfigService } from '../../config.service';

type PaymentsState = 'loading' | 'error' | 'no-matches' | 'matches';

interface PaymentRowView {
  dateAmountItems: TermValueItem[];
  detailLabel?: string;
}

@Component({
  selector: 'app-payments',
  imports: [CardComponent, CardBodyComponent, StateMessageComponent, TermValueComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'closeMenu()',
  },
  styles: [
    `
      .payment-detail-label {
        font-style: italic;
        padding: 4px 8px;
      }
    `,
  ],
  template: `
    <groundworks-card class="h-100" [cardClasses]="['h-100']" [title]="'Payments'">
      <groundworks-card-body class="d-flex flex-column h-100">
        <div class="flex-grow-1 overflow-y-auto min-h-0">
          @switch (state()) { @case ('loading') {
          <groundworks-state-message type="loading" message="Loading payments..." class="d-flex" />
          } @case ('error') {
          <groundworks-state-message
            type="error"
            message="Error loading payments."
            class="d-flex"
          />
          } @case ('no-matches') {
          <groundworks-state-message
            type="no-matches"
            message="No payments recorded."
            class="d-flex"
          />
          } @case ('matches') { @for (row of receivedRows(); track $index) {
          <groundworks-term-value [items]="row.dateAmountItems" />
          @if (row.detailLabel) {
          <div class="payment-detail-label" data-testid="payment-detail-label">
            {{ row.detailLabel }}
          </div>
          } } @if (pendingRows().length > 0) {
          <div class="fst-italic" data-testid="pending-credits">
            @for (row of pendingRows(); track $index) {
            <groundworks-term-value [items]="row.dateAmountItems" />
            @if (row.detailLabel) {
            <div class="payment-detail-label">{{ row.detailLabel }}</div>
            } }
          </div>
          }
          <groundworks-term-value [items]="paymentsReceivedItems()" />
          @if (showTotalBalanceDue) {
          <groundworks-term-value [items]="totalBalanceDueItems()" />
          } } }
        </div>

        <div class="d-flex justify-content-end mt-auto pt-3">
          <div class="dropdown position-relative" #menuRef>
            <button
              type="button"
              class="btn btn-sm p-1"
              data-testid="payments-menu-toggle"
              aria-label="More payment actions"
              aria-haspopup="menu"
              [attr.aria-expanded]="isMenuOpen()"
              (click)="toggleMenu()"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 16 16"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z"
                />
              </svg>
            </button>
            @if (isMenuOpen()) {
            <div class="dropdown-menu show position-absolute end-0 start-auto" role="menu">
              <button
                type="button"
                class="dropdown-item"
                role="menuitem"
                data-testid="send-deposit-request"
                [disabled]="!customerId()"
                (click)="onSendDepositRequest()"
              >
                Send Deposit Request
              </button>
              <button
                type="button"
                class="dropdown-item"
                role="menuitem"
                data-testid="manual-post-payment"
                [disabled]="!opportunityId()"
                (click)="onManualPostPayment()"
              >
                Manual Post Payment
              </button>
            </div>
            }
          </div>
        </div>
      </groundworks-card-body>
    </groundworks-card>
  `,
})
export class PaymentsComponent {
  private readonly paymentService = inject(PaymentService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(ModalService);

  // "Total Balance Due" (whole-job balance) is gated behind the enable_total_balance_due
  // env flag so it can be shown/hidden independently of the payment line items.
  readonly showTotalBalanceDue = inject(ConfigService).enableTotalBalanceDue;

  // Opportunity whose payment summary is displayed. Sourced from Groundworks.Jobs
  // via the Sales backend proxy (GET /v1/payments/summary/{opportunityId}).
  readonly opportunityId = input<string>();
  readonly customerId = input<number>();

  private readonly menuRef = viewChild<ElementRef<HTMLElement>>('menuRef');

  private readonly summary = signal<PaymentSummary | undefined>(undefined);
  private readonly loading = signal(false);
  private readonly error = signal(false);
  readonly isMenuOpen = signal(false);

  readonly state = computed<PaymentsState>(() => {
    if (this.loading()) return 'loading';
    if (this.error()) return 'error';
    const s = this.summary();
    if (!s) return 'no-matches';
    const hasRows = (s.payments?.length ?? 0) > 0 || (s.pendingCredits?.length ?? 0) > 0;
    return hasRows ? 'matches' : 'no-matches';
  });

  readonly receivedRows = computed<PaymentRowView[]>(() =>
    (this.summary()?.payments ?? []).map((p) => this.toRow(p)),
  );

  readonly pendingRows = computed<PaymentRowView[]>(() =>
    (this.summary()?.pendingCredits ?? []).map((p) => this.toRow(p)),
  );

  readonly paymentsReceivedItems = computed<TermValueItem[]>(() => [
    {
      term: 'Payments Received',
      value: this.money(this.summary()?.paymentsReceived ?? 0),
      variant: 'subtotal',
    },
  ]);

  readonly totalBalanceDueItems = computed<TermValueItem[]>(() => [
    {
      term: 'Total Balance Due',
      value: this.balanceDisplay(this.summary()?.totalBalanceDue),
      variant: 'total',
    },
  ]);

  constructor() {
    effect(() => {
      const id = this.opportunityId();
      if (!id) {
        this.summary.set(undefined);
        this.loading.set(false);
        this.error.set(false);
        return;
      }
      this.load(id);
    });
  }

  toggleMenu(): void {
    this.isMenuOpen.update((open) => !open);
  }

  closeMenu(): void {
    this.isMenuOpen.set(false);
  }

  onDocumentClick(event: MouseEvent): void {
    if (!this.isMenuOpen()) return;
    const host = this.menuRef()?.nativeElement;
    if (host && !host.contains(event.target as Node)) {
      this.closeMenu();
    }
  }

  onSendDepositRequest(): void {
    const opportunityId = this.opportunityId();
    const customerId = this.customerId();
    if (!opportunityId || !customerId) return;
    this.closeMenu();

    const modalRef = this.modalService.openSendDepositRequestModal(opportunityId, customerId);
    modalRef.result.then(
      () => {
        this.modalService.openSuccessModal(
          'Deposit request sent',
          'The customer has been texted a payment link. The deposit is confirmed once they pay.',
        );
        this.reload();
      },
      () => {
        // dismissed / cancelled — nothing to do
      },
    );
  }

  onManualPostPayment(): void {
    const opportunityId = this.opportunityId();
    if (!opportunityId) return;
    this.closeMenu();

    const modalRef = this.modalService.openManualPostPaymentModal(opportunityId);
    modalRef.result.then(
      () => {
        this.modalService.openSuccessModal(
          'Payment posted',
          'The manual payment has been recorded.',
        );
        this.reload();
      },
      () => {
        // dismissed / cancelled — nothing to do
      },
    );
  }

  private toRow(item: PaymentLineItem): PaymentRowView {
    return {
      dateAmountItems: [
        {
          term: item.date ? formatDate(item.date, 'MM/dd/yyyy', 'en-US', 'UTC') : '',
          value: this.money(item.amount),
        },
      ],
      detailLabel: item.label || undefined,
    };
  }

  private money(value: number): string {
    return formatCurrency(value, 'en-US', '$', 'USD');
  }

  // Balance is null/undefined when Jobs has no job for the opportunity — show "—"
  // rather than a misleading $0.00.
  private balanceDisplay(value: number | null | undefined): string {
    return value == null ? '—' : this.money(value);
  }

  private reload(): void {
    const id = this.opportunityId();
    if (id) this.load(id);
  }

  private load(opportunityId: string): void {
    this.loading.set(true);
    this.error.set(false);
    this.paymentService
      .getPaymentSummary(opportunityId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (summary) => {
          if (this.opportunityId() !== opportunityId) return;
          this.summary.set(summary);
          this.loading.set(false);
        },
        error: (err: { status?: number }) => {
          if (this.opportunityId() !== opportunityId) return;
          this.loading.set(false);
          if (err?.status === 404) {
            this.summary.set(undefined);
          } else {
            this.error.set(true);
          }
        },
      });
  }
}
